import React from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
  Avatar,
  Box,
  Typography,
  Stack,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PeopleIcon from "@mui/icons-material/People";
import HandshakeIcon from "@mui/icons-material/Handshake";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CategoryIcon from "@mui/icons-material/Category";
import CheckroomIcon from "@mui/icons-material/Checkroom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { Link, useLocation, useParams } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const drawerWidth = 260;

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
  desktopOpen?: boolean;
  userDoc?: any;
  onAvatarClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function Sidebar({
  mobileOpen,
  onClose,
  desktopOpen = true,
  userDoc: propUserDoc,
  onAvatarClick,
}: SidebarProps) {
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { userDoc: authUserDoc } = useAuth();

  const userDoc = propUserDoc || authUserDoc;
  const userRole = userDoc?.role;

  const menuItems = [
    {
      id: "dashboard",
      text: "Panel General",
      icon: <DashboardIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/dashboard`,
    },
    {
      id: "web-credits",
      text: "Créditos & Estudios",
      icon: <CreditCardIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-credits`,
    },
    {
      id: "web-appointments",
      text: "Citas & Asesorías",
      icon: <EventAvailableIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-appointments`,
    },
    {
      id: "web-allies",
      text: "Aliados Estratégicos",
      icon: <HandshakeIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-allies`,
    },
    {
      id: "web-users",
      text: "Usuarios del Portal",
      icon: <PeopleIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-users`,
    },
    {
      id: "web-bank-forms",
      text: "Matrices & Bancos",
      icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-bank-forms`,
    },
    {
      id: "web-categories",
      text: "Líneas & Categorías",
      icon: <CategoryIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-categories`,
    },
    {
      id: "web-quotes",
      text: "Cotizaciones",
      icon: <RequestQuoteIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-quotes`,
    },
    {
      id: "web-products",
      text: "Productos Web",
      icon: <CheckroomIcon sx={{ fontSize: 18 }} />,
      path: `/${restaurantId}/web-products`,
    },
  ];

  const filterItem = (item: any): boolean => {
    if (item.id === "dashboard") return true;
    if (userDoc?.isAdmin === true || userRole === "admin") return true;
    if (userDoc?.permissions?.allowedPages && userDoc.permissions.allowedPages.length > 0) {
      return userDoc.permissions.allowedPages.includes(item.id);
    }
    return true;
  };

  const filteredMenuItems = menuItems.filter(filterItem);

  const userName: string = userDoc?.name || userDoc?.displayName || userDoc?.email?.split("@")[0] || "Administrador";
  const userEmail: string = userDoc?.email || "admin@nextconnection.com";
  const avatarLetter = userName.charAt(0).toUpperCase();

  const sidebarContent = (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#ffffff",
        py: 2.5,
        px: 2,
        boxSizing: "border-box",
      }}
    >
      {/* 🔹 Brand Header: Next Connection Group */}
      <Box
        component={Link}
        to={`/${restaurantId}/dashboard`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          textDecoration: "none",
          mb: 3,
          px: 1,
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "12px",
            bgcolor: "#0f172a",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 12px rgba(15, 23, 42, 0.15)",
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 20, color: "#34d399" }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: "1.05rem",
              color: "#0f172a",
              letterSpacing: "-0.3px",
              lineHeight: 1.1,
              whiteSpace: "nowrap",
            }}
          >
            Next Connection
          </Typography>
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 600,
              color: "#64748b",
              letterSpacing: "0.2px",
              textTransform: "uppercase",
              mt: 0.2,
            }}
          >
            Panel Administrativo
          </Typography>
        </Box>
      </Box>

      {/* 🔹 Navigation Items */}
      <List sx={{ p: 0, flexGrow: 1 }}>
        {filteredMenuItems.map((item) => {
          const isSelected =
            (item.id === "dashboard" &&
              (location.pathname === `/${restaurantId}` ||
                location.pathname === `/${restaurantId}/` ||
                location.pathname === `/${restaurantId}/dashboard`)) ||
            location.pathname === item.path ||
            location.pathname.startsWith(`${item.path}/`);

          return (
            <ListItemButton
              key={item.id}
              component={Link}
              to={item.path}
              selected={isSelected}
              onClick={isMobile ? onClose : undefined}
              sx={{
                borderRadius: "12px",
                my: 0.4,
                py: 1.1,
                px: 1.8,
                transition: "all 0.18s ease-in-out",
                bgcolor: isSelected ? "#0f172a !important" : "transparent",
                color: isSelected ? "#ffffff !important" : "#475569",
                boxShadow: isSelected ? "0 4px 12px rgba(15, 23, 42, 0.12)" : "none",
                "&:hover": {
                  bgcolor: isSelected ? "#0f172a" : "#f1f5f9",
                  color: isSelected ? "#ffffff" : "#0f172a",
                  "& .MuiListItemIcon-root": {
                    color: isSelected ? "#ffffff" : "#0f172a",
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  color: isSelected ? "#34d399" : "#64748b",
                  transition: "color 0.15s ease",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: "0.86rem",
                  fontWeight: isSelected ? 800 : 600,
                  noWrap: true,
                  letterSpacing: "-0.2px",
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* 🔹 User Profile Card at Bottom */}
      <Box
        onClick={onAvatarClick}
        sx={{
          mt: "auto",
          pt: 2,
          borderTop: "1px solid #f1f5f9",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          cursor: "pointer",
          borderRadius: "14px",
          p: 1.2,
          transition: "background 0.15s ease",
          "&:hover": {
            bgcolor: "#f8fafc",
          },
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: "#0f172a",
            color: "#ffffff",
            fontSize: "0.85rem",
            fontWeight: 800,
            boxShadow: "0 2px 6px rgba(15, 23, 42, 0.15)",
          }}
        >
          {avatarLetter}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "0.85rem",
              color: "#0f172a",
              lineHeight: 1.2,
            }}
            noWrap
          >
            {userName}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "#64748b",
              fontWeight: 500,
              mt: 0.2,
            }}
            noWrap
          >
            {userEmail}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      {/* MOBILE DRAWER */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              border: "none",
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      )}

      {/* DESKTOP INTEGRATED SIDEBAR */}
      {!isMobile && (
        <Box
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            borderRight: "1px solid #e2e8f0",
            bgcolor: "#ffffff",
            display: "flex",
            flexDirection: "column",
            position: "sticky",
            top: 0,
            height: "100vh",
          }}
        >
          {sidebarContent}
        </Box>
      )}
    </>
  );
}
